import { useState, useMemo } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";

const fmt = n => "$" + Math.round(n).toLocaleString("es-CL");

export default function TendenciaDiaria({ datos }) {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [vista, setVista] = useState("pedidos");

  const datosFiltrados = useMemo(() => {
    if (!datos || datos.length === 0) return [];
    return datos
      .filter(d => {
        const f = d.fecha?.slice(0, 10);
        if (desde && f < desde) return false;
        if (hasta && f > hasta) return false;
        return true;
      })
      .map(d => ({
        fecha:       d.fecha?.slice(5, 10) ?? d.fecha,
        pedidos:     d.total_pedidos ?? 0,
        confirmados: d.confirmados ?? 0,
        fallidos:    d.fallidos ?? 0,
        ingresos:    Math.round(d.ingresos_reales ?? 0),
        gasto:       Math.round(d.gasto_meta ?? 0),
        tasa:        parseFloat((d.tasa_entrega ?? 0).toFixed(1)),
      }));
  }, [datos, desde, hasta]);

  const totales = useMemo(() => ({
    pedidos:     datosFiltrados.reduce((s, d) => s + d.pedidos, 0),
    confirmados: datosFiltrados.reduce((s, d) => s + d.confirmados, 0),
    fallidos:    datosFiltrados.reduce((s, d) => s + d.fallidos, 0),
    ingresos:    datosFiltrados.reduce((s, d) => s + d.ingresos, 0),
    gasto:       datosFiltrados.reduce((s, d) => s + d.gasto, 0),
    tasaProm:    datosFiltrados.length > 0
      ? (datosFiltrados.reduce((s, d) => s + d.tasa, 0) / datosFiltrados.length).toFixed(1)
      : 0,
  }), [datosFiltrados]);

  const inputStyle = {
    fontSize: 12, padding: "5px 10px", borderRadius: 8,
    border: "0.5px solid #ddd", background: "transparent",
    color: "inherit", cursor: "pointer",
  };

  const tabStyle = active => ({
    fontSize: 11, padding: "4px 12px", borderRadius: 20, cursor: "pointer",
    border: active ? "none" : "0.5px solid #ddd",
    background: active ? "#111" : "transparent",
    color: active ? "#fff" : "#666",
  });

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "1rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: "1rem" }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Tendencia diaria
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "#888" }}>Desde</span>
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={inputStyle} />
          <span style={{ fontSize: 11, color: "#888" }}>Hasta</span>
          <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={inputStyle} />
          {(desde || hasta) && (
            <button onClick={() => { setDesde(""); setHasta(""); }} style={{ ...inputStyle, color: "#A32D2D" }}>
              ✕ Limpiar
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0,1fr))", gap: 8, marginBottom: "1rem" }}>
        {[
          { label: "Pedidos",      value: totales.pedidos },
          { label: "Confirmados",  value: totales.confirmados },
          { label: "Fallidos",     value: totales.fallidos },
          { label: "Ingresos",     value: fmt(totales.ingresos) },
          { label: "Gasto Meta",   value: fmt(totales.gasto) },
          { label: "Tasa entrega", value: totales.tasaProm + "%" },
        ].map(k => (
          <div key={k.label} style={{ background: "#f7f7f5", borderRadius: 8, padding: "8px 10px" }}>
            <div style={{ fontSize: 10, color: "#888", marginBottom: 3 }}>{k.label}</div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: "0.85rem" }}>
        <button style={tabStyle(vista === "pedidos")}  onClick={() => setVista("pedidos")}>Pedidos</button>
        <button style={tabStyle(vista === "ingresos")} onClick={() => setVista("ingresos")}>Ingresos vs Gasto</button>
        <button style={tabStyle(vista === "tasa")}     onClick={() => setVista("tasa")}>Tasa de entrega</button>
      </div>

      {vista === "pedidos" && (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={datosFiltrados} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,135,128,0.2)" vertical={false} />
            <XAxis dataKey="fecha" tick={{ fontSize: 9, fill: "#888" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 9, fill: "#888" }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
            <Bar dataKey="confirmados" name="Confirmados" stackId="a" fill="#1D9E75" />
            <Bar dataKey="fallidos"    name="Fallidos"    stackId="a" fill="#E24B4A" radius={[3, 3, 0, 0]} />
            <Line dataKey="pedidos" name="Total pedidos" type="monotone" stroke="#534AB7" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}

      {vista === "ingresos" && (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={datosFiltrados} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,135,128,0.2)" vertical={false} />
            <XAxis dataKey="fecha" tick={{ fontSize: 9, fill: "#888" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 9, fill: "#888" }} tickFormatter={v => "$" + Math.round(v / 1000) + "K"} />
            <Tooltip formatter={v => "$" + v.toLocaleString("es-CL")} contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
            <Bar dataKey="ingresos" name="Ingresos reales" fill="#1D9E75" radius={[3, 3, 0, 0]} opacity={0.85} />
            <Bar dataKey="gasto"    name="Gasto Meta"      fill="#E24B4A" radius={[3, 3, 0, 0]} opacity={0.75} />
          </ComposedChart>
        </ResponsiveContainer>
      )}

      {vista === "tasa" && (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={datosFiltrados} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,135,128,0.2)" vertical={false} />
            <XAxis dataKey="fecha" tick={{ fontSize: 9, fill: "#888" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 9, fill: "#888" }} domain={[0, 100]} tickFormatter={v => v + "%"} />
            <Tooltip formatter={v => v + "%"} contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
            <Line dataKey="tasa" name="Tasa de entrega %" type="monotone" stroke="#1D9E75" strokeWidth={2} dot={{ r: 3, fill: "#1D9E75" }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}

      <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>
        {datosFiltrados.length} días en el período seleccionado
      </div>
    </div>
  );
}
